'use client';

/**
 * EMAIL OPS — Compose / Contacts / Senders, same mailer APIs as classic /email:
 * /api/email/projects (sender identities), /api/email/contacts, /api/email/send.
 */
import { useEffect, useState } from 'react';
import { Glass, Pill, StatusChip, EmptyState, ExecuteBtn, mono, display } from '@/future/primitives';

type Sender = { name: string; from_addr: string; reply_to: string; base_url: string };
type Contact = { id: string; project: string; email: string; name: string; company: string | null; status: string; last_contacted_at: string | null };

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.09)',
  borderRadius: 8, padding: '10px 14px', color: 'var(--uif-t1)', outline: 'none',
  fontSize: 24, width: '100%',
};

/* ---------------- SENDERS ---------------- */
export function SendersSurface() {
  const [senders, setSenders] = useState<Sender[] | null>(null);
  useEffect(() => {
    fetch('/api/email/projects').then(r => r.json()).then(setSenders).catch(() => setSenders([]));
  }, []);
  if (senders === null) return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING IDENTITIES…</div>;
  if (!senders.length) return <EmptyState what="NO SENDER IDENTITIES" action="Register a project in beast-mailer to send." />;
  return (
    <div className="mx-auto max-w-3xl flex flex-col gap-3">
      {senders.map(s => (
        <Glass key={s.name} className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ border: '1px solid rgba(126,205,231,.3)', ...mono(20), color: 'var(--uif-cy-interactive)' }}>
            {s.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div style={{ ...display(28), color: 'var(--uif-t1)' }}>{s.name.toUpperCase()}</div>
            <div className="truncate" style={{ ...mono(18), color: 'var(--uif-t4)' }}>{s.from_addr}</div>
            <div className="truncate" style={{ ...mono(16), color: 'var(--uif-t5)' }}>reply-to {s.reply_to} · {s.base_url}</div>
          </div>
          <div className="ml-auto"><StatusChip label="ACTIVE" kind="good" /></div>
        </Glass>
      ))}
    </div>
  );
}

/* ---------------- CONTACTS ---------------- */
export function ContactsSurface({ scope }: { scope?: string[] | null }) {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    const url = scope?.length ? `/api/email/contacts?project=${scope.join(',')}` : '/api/email/contacts';
    fetch(url).then(r => r.json()).then(d => setContacts(Array.isArray(d) ? d : [])).catch(() => setContacts([]));
  }, [scope]);
  if (contacts === null) return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING CONTACT BOOK…</div>;
  const shown = contacts.filter(c => !q || `${c.name} ${c.email} ${c.company}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mx-auto max-w-4xl flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <input placeholder="SEARCH CONTACTS…" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle, maxWidth: 320 }} />
        <div className="ml-auto" style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{shown.length} / {contacts.length}</div>
      </div>
      {!shown.length && <EmptyState what="NO CONTACTS" action="The outreach book is empty for this scope." />}
      <Glass className="p-0 overflow-hidden">
        {shown.slice(0, 200).map(c => (
          <div key={c.id} className="flex items-center gap-4 px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
            <div className="w-40 truncate" style={{ ...display(24), color: 'var(--uif-t1)' }}>{c.name || '—'}</div>
            <div className="w-56 truncate" style={{ ...mono(18), color: 'var(--uif-t4)' }}>{c.email}</div>
            <div className="w-36 truncate" style={{ ...mono(16), color: 'var(--uif-t5)' }}>{c.company || ''}</div>
            <StatusChip label={(c.status || 'new').toUpperCase()} kind={c.status === 'replied' ? 'good' : c.status === 'emailed' ? 'warn' : 'dim'} />
            <div className="ml-auto" style={{ ...mono(16), color: 'var(--uif-t6)' }}>{c.project.toUpperCase()}</div>
          </div>
        ))}
      </Glass>
    </div>
  );
}

/* ---------------- COMPOSE ---------------- */
export function ComposeSurface({ scope }: { scope?: string[] | null }) {
  const [senders, setSenders] = useState<Sender[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [err, setErr] = useState('');
  const [attachments, setAttachments] = useState<{ filename: string; content: string }[]>([]);

  useEffect(() => {
    fetch('/api/email/projects').then(r => r.json()).then((all: Sender[]) => {
      const list = scope?.length ? all.filter(s => scope.includes(s.name)) : all;
      setSenders(list);
      setFrom(f => f || list[0]?.name || '');
    }).catch(() => {});
  }, [scope]);

  // Reply/Follow-up handoff from the INBOX message slab (classic parity).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('uif:compose:prefill');
      if (!raw) return;
      sessionStorage.removeItem('uif:compose:prefill');
      const p = JSON.parse(raw);
      if (p.project) setFrom(p.project);
      if (p.to) setTo(p.to);
      if (p.subject) setSubject(p.subject);
      if (p.body) setBody(p.body);
    } catch { /* ignore */ }
  }, []);

  const send = async () => {
    if (!from || !to || !subject || !body) { setErr('ALL FIELDS REQUIRED'); setState('error'); return; }
    setState('sending'); setErr('');
    try {
      const r = await fetch('/api/email/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project: from, to, subject, html: body.replace(/\n/g, '<br/>'), attachments: attachments.length ? attachments : undefined }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setState('sent'); setTo(''); setSubject(''); setBody(''); setAttachments([]);
      setTimeout(() => setState('idle'), 3000);
    } catch (e: any) { setErr(String(e.message || e).toUpperCase()); setState('error'); }
  };

  return (
    <div className="mx-auto max-w-2xl flex flex-col gap-3">
      <Glass className="p-6 flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>SEND AS</div>
          {senders.map(s => (
            <Pill key={s.name} onClick={() => setFrom(s.name)} active={from === s.name} label={s.name.toUpperCase()} />
          ))}
          {!senders.length && <div style={{ ...mono(18), color: 'var(--uif-t5)' }}>NO SENDER IDENTITY FOR THIS SCOPE</div>}
        </div>
        <input placeholder="TO — email@domain.com" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        <input placeholder="SUBJECT" value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />
        <textarea placeholder="MESSAGE…" value={body} onChange={e => setBody(e.target.value)} rows={10} style={{ ...inputStyle, resize: 'vertical' }} />
        <div className="flex items-center gap-2 flex-wrap">
          <label style={{ ...mono(15, '.16em'), color: 'var(--uif-t4)', border: '1px dashed rgba(125,232,255,.3)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer' }}>
            📎 ATTACH FILES
            <input type="file" multiple className="hidden" onChange={async e => {
              const files = Array.from(e.target.files || []);
              const read = await Promise.all(files.map(f => new Promise<{ filename: string; content: string }>((res) => {
                const r = new FileReader();
                r.onload = () => res({ filename: f.name, content: String(r.result).split(',')[1] });
                r.readAsDataURL(f);
              })));
              setAttachments(a => [...a, ...read]);
              e.target.value = '';
            }} />
          </label>
          {attachments.map((a, i) => (
            <span key={i} style={{ ...mono(14, '.1em'), color: 'var(--uif-t3)', border: '1px solid rgba(125,232,255,.2)', borderRadius: 999, padding: '4px 10px' }}>
              {a.filename} <button onMouseUp={() => setAttachments(x => x.filter((_, j) => j !== i))} style={{ color: 'var(--uif-t5)', marginLeft: 4 }}>×</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <ExecuteBtn label={state === 'sending' ? 'TRANSMITTING…' : state === 'sent' ? 'SENT ✓' : 'TRANSMIT'} onRun={send} />
          {state === 'error' && <div style={{ ...mono(16, '.14em'), color: 'var(--uif-bad)' }}>{err}</div>}
          {from && <div className="ml-auto" style={{ ...mono(16), color: 'var(--uif-t6)' }}>{senders.find(s => s.name === from)?.from_addr}</div>}
        </div>
      </Glass>
    </div>
  );
}
